import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { pageContext } from 'ctm-util-functions';
import styles from './filters_animated_wrapper.css';
import { ScrollWrapper } from '../../../../higher_order_components/scroll_wrapper';

/**
 * todo: rename component to match file name
 * A toggle enabled container which houses the results filters and snapshots
 * @param {Object} props The properties
 * @param {Boolean} props.visuallyHidden - visually hidden
 * @param {Boolean} props.isVisible - container visibility
 * @param {Node} props.children - react children nodes
 * @returns {Node} The results animated wrapper
 */
const ResultsAnimatedWrapper = ({ visuallyHidden, isVisible, children }) => {
    const rootRef = useRef(null);
    const contentContainerRef = useRef(null);
    const stickyElementsAboveRef = useRef([]);
    const collapsibleElementsAboveRef = useRef([]);
    const firstElementBelowRef = useRef(null);
    const mutationObserverRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const observedElementsRef = useRef(new Set());
    const animationFrameRef = useRef(null);

    /**
     * Allows registered event handlers to access the most recent consolidated value of isVisible and visuallyHidden.
     * This is necessary since these handlers would otherwise remain registered with old state values until the
     * useEffect could update them. This is a minor issue because some handlers can be triggered by the dom changes from a
     * re-render, which might cause a race condition with the useEffect.
     */
    const isVisibleRef = useRef(true);
    isVisibleRef.current = isVisible && !visuallyHidden;

    const vertical = pageContext.getVertical();

    useEffect(() => {
        // elements should be loaded into their respective refs before resize observer is set up and updatePosition is run
        updateMutationObserver();
        updateElements();
        updateResizeObserver();
        updatePosition();

        return () => {
            cleanupObservers();
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [visuallyHidden, isVisible]);

    /**
     * POTENTIAL FUTURE ISSUE: If a new component is added above the content section which is sticky in behaviour but does not use 'position: sticky'
     */
    const updateElements = () => {
        if (!isVisibleRef.current) return;

        contentContainerRef.current = document.querySelector('.container_full_width_body_results');
        if (!contentContainerRef.current) return;

        const elementsAbove = [];
        let sibling = contentContainerRef.current.previousElementSibling;
        while (sibling) {
            elementsAbove.push(sibling);
            sibling = sibling.previousElementSibling;
        }
        stickyElementsAboveRef.current = elementsAbove.filter((el) => window.getComputedStyle(el).position === 'sticky');
        collapsibleElementsAboveRef.current = elementsAbove.filter((el) => window.getComputedStyle(el).position !== 'sticky');

        firstElementBelowRef.current = contentContainerRef.current.nextElementSibling;
    };

    /**
     * The marginTop and maxHeight of the sidebar is calculated based on the offset and height of surrounding elements. These observers ensure
     * that marginTop and maxHeight are recalculated and applied when the DOM changes in a way that should affect those values (e.g. element added to
     * page or height increase for a surrounding element).
     */
    const updateMutationObserver = () => {
        if (!isVisibleRef.current) return;

        const handleDomMutation = () => {
            updateElements();
            updateResizeObserver();
            updatePosition();
        };

        if (!mutationObserverRef.current) {
            mutationObserverRef.current = new MutationObserver(handleDomMutation);
            mutationObserverRef.current.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    };

    const updateResizeObserver = () => {
        if (!isVisibleRef.current) return;

        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(updatePosition);
        }

        const elementsToObserve = new Set(
            [contentContainerRef.current, ...stickyElementsAboveRef.current, ...collapsibleElementsAboveRef.current]
                .filter((el) => el !== null)
        );

        observedElementsRef.current.forEach((el) => {
            if (!elementsToObserve.has(el)) {
                resizeObserverRef.current.unobserve(el);
                observedElementsRef.current.delete(el);
            }
        });

        elementsToObserve.forEach((el) => {
            if (!observedElementsRef.current.has(el)) {
                resizeObserverRef.current.observe(el);
                observedElementsRef.current.add(el);
            }
        });
    };

    const cleanupObservers = () => {
        mutationObserverRef.current?.disconnect();
        mutationObserverRef.current = null;
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = null;
        observedElementsRef.current.clear();
    };

    const updatePosition = useCallback(() => {
        if (!isVisibleRef.current) return;

        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

        animationFrameRef.current = requestAnimationFrame(() => {
            const viewportTopOffset = window.scrollY;
            const viewportBottomOffset = window.scrollY + window.innerHeight;

            const stickyElementsAboveHeight = stickyElementsAboveRef.current.reduce((acc, el) => (acc + el.getBoundingClientRect().height), 0);
            const collapsibleElementsAboveHeight = collapsibleElementsAboveRef.current.reduce((acc, el) => (acc + el.getBoundingClientRect().height), 0);
            const visibleCollapsibleElementsAboveHeight = Math.max(0, collapsibleElementsAboveHeight - viewportTopOffset);
            const visibleElementsAboveHeight = stickyElementsAboveHeight + visibleCollapsibleElementsAboveHeight;

            const contentContainerBottomGap = vertical === 'home-contents-insurance' ? 0 : 15;
            const firstElementBelowTopOffset = firstElementBelowRef.current ? firstElementBelowRef.current.offsetTop : (viewportBottomOffset + contentContainerBottomGap);

            const sidebarTopOffset = (viewportTopOffset + visibleElementsAboveHeight);
            const sidebarBottomOffset = Math.min(viewportBottomOffset, firstElementBelowTopOffset - contentContainerBottomGap);

            const sidebarTopMargin = visibleElementsAboveHeight;
            const sidebarMaxHeight = Math.max(0, sidebarBottomOffset - sidebarTopOffset);

            if (rootRef.current) {
                rootRef.current.style.marginTop = `${sidebarTopMargin}px`;
                rootRef.current.style.maxHeight = `${sidebarMaxHeight}px`;
            }
        });
    }, []);

    const style = {
        scrollbarColor: 'track_color transparent',
        scrollbarWidth: 'thin'
    };

    return (
        <ScrollWrapper handleScroll={updatePosition}>
            <div
                ref={rootRef}
                style={style}
                className={classNames({
                    [styles.root]: true,
                    [styles.slide_out]: !isVisible,
                    [styles.visually_hidden]: visuallyHidden,
                    [styles.position_abs]: false,
                    [styles.max_height]: true })}>
                {children}
            </div>
        </ScrollWrapper>
    );
};

ResultsAnimatedWrapper.propTypes = {
    visuallyHidden: PropTypes.bool,
    isVisible: PropTypes.bool.isRequired,
    children: PropTypes.array
};

export { ResultsAnimatedWrapper };
